import { useMemo } from 'react';
import { generateGroupbyHint, getTextWidth } from '../../utils';
import useBuilderInfoDisplay from '../../utils/useBuilderInfoDisplay';
import * as ChartGroupsUI from '../ChartGroups/ChartGroupsUI';
import * as CompactChartGroupsUI from './CompactChartGroupsUI';
import useToggleSlice from '../../hooks/useToggleSlice';
import styled from 'styled-components';
import CompactChartGroupMenu from './CompactChartGroupMenu';
import CompactChartGroupItemIcon from './CompactChartGroupItemIcon';
import KubitTooltip from '../../../KubitTooltip';
import { useChartContext } from '../../ChartContext';

interface IProps {
  analysisId: string;
}

const ICON_WIDTH = 12;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

export default ({ analysisId }: IProps) => {
  const {
    settings: { chartWidth },
    colorConfig,
    displayedSlices,
  } = useChartContext();

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const visibleSliceCount = useMemo(() => {
    let currentWidth = 75; // more slices width
    let sliceCount = 0;

    for (let item of displayedSlices) {
      const textWidth = getTextWidth(item.name, '12pt bold');
      const totalWidth = textWidth + ICON_WIDTH + 4 + 16; // gap + margin
      if (currentWidth + totalWidth <= chartWidth) {
        currentWidth += totalWidth;
        sliceCount += 1;
      } else {
        break;
      }
    }
    return sliceCount;
  }, [displayedSlices, chartWidth]);

  const toggleSlice = useToggleSlice(analysisId);

  return (
    <Container>
      {builderInfoDisplay && (
        <ChartGroupsUI.BuilderInfoLabel>
          {builderInfoDisplay}
        </ChartGroupsUI.BuilderInfoLabel>
      )}
      <CompactChartGroupsUI.HorizontalContainer>
        {displayedSlices.slice(0, visibleSliceCount).map((slice) => (
          <KubitTooltip
            key={`${analysisId}-${slice.key}`}
            // if hint is undefined, or hint array is empty, then hide hint
            disabled={!slice.hint || slice.hint.length === 0}
            content={generateGroupbyHint(slice.hint, slice.rank)}
            placement="top"
            noOpenDelay={true}
          >
            <CompactChartGroupsUI.GroupItem
              onClick={() => toggleSlice(slice.key, slice.isVisible)}
            >
              <CompactChartGroupItemIcon
                slice={slice}
                colorConfig={colorConfig}
              />
              <CompactChartGroupsUI.GroupItemText $isVisible={slice.isVisible}>
                {slice.name}
              </CompactChartGroupsUI.GroupItemText>
            </CompactChartGroupsUI.GroupItem>
          </KubitTooltip>
        ))}
        {visibleSliceCount < displayedSlices.length && (
          <CompactChartGroupMenu
            label={`+ ${displayedSlices.length - visibleSliceCount} more`}
            displayedSlices={displayedSlices.slice(visibleSliceCount)}
            colorConfig={colorConfig}
            onToggleSlice={toggleSlice}
          />
        )}
      </CompactChartGroupsUI.HorizontalContainer>
    </Container>
  );
};
